import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState<string | null>(slug || null);

  useEffect(() => {
    setActiveCategory(slug || null);
  }, [slug]);

  useEffect(() => {
    document.title = t('seo.categoryTitle');
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', t('seo.categoryDesc'));
  }, [t]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  const filteredArticles = activeCategory
    ? articles.filter((a) => a.category === activeCategory)
    : articles;

  const breadcrumbItems = activeCategory
    ? [
        { label: t('breadcrumb.category'), to: '/category' },
        { label: currentCategory?.name || '' },
      ]
    : [{ label: t('breadcrumb.category') }];

  return (
    <div className="pt-16">
      <Breadcrumb items={breadcrumbItems} />

      <section className="w-full py-8 md:py-12 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-10">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              {currentCategory ? currentCategory.name : t('category.title')}
            </h1>
            <p className="mt-2 text-sm text-foreground-600 max-w-xl mx-auto">
              {currentCategory ? currentCategory.description : t('category.desc')}
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 mb-8 md:mb-10">
            <Link
              to="/category"
              className={`px-4 py-2 rounded-full text-xs md:text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                !activeCategory
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {t('category.allCategories')}
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-xs md:text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-100 rounded-xl overflow-hidden hover:scale-[1.01] transition-all duration-300 cursor-pointer"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={article.cover}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                      title={article.title}
                    />
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/50 to-transparent">
                      <div className="flex gap-2">
                        {article.tags.slice(0, 2).map((tag) => (
                          <span key={tag} className="px-2 py-0.5 bg-background-50/90 backdrop-blur-sm rounded-full text-xs text-primary-600">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm md:text-base font-semibold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors duration-300">
                      {article.title}
                    </h3>
                    <p className="mt-2 text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="mt-3 flex items-center justify-between text-xs text-foreground-400">
                      <span>{article.date}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {article.views.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-image-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">{t('category.noArticles')}</p>
              <Link
                to="/category"
                className="inline-flex items-center gap-1.5 mt-4 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                {t('category.backToCategories')}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}